import { useEffect, useRef, useState } from 'react';

export interface WsMessage {
  channel: 'bot:log' | 'bot:event' | 'meta' | 'runs' | 'changes';
  payload: unknown;
}

/*
 * One /ws/live socket per tab, shared by every hook below: opened with the
 * first subscriber, closed with the last, auto-reconnecting with a linear
 * backoff (max 30 s). Status listeners hear every (re)connect — a chance to
 * refetch what may have been missed while disconnected.
 */
const messageListeners = new Set<(msg: WsMessage) => void>();
const statusListeners = new Set<(connected: boolean) => void>();
let socket: WebSocket | null = null;
let connected = false;
let users = 0;
let attempts = 0;
let retryTimer: number | undefined;

function setConnected(value: boolean) {
  connected = value;
  for (const listener of statusListeners) listener(value);
}

function connect() {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const ws = new WebSocket(`${proto}://${window.location.host}/ws/live`);
  socket = ws;
  ws.onopen = () => {
    attempts = 0;
    setConnected(true);
  };
  ws.onmessage = (ev) => {
    let msg: WsMessage;
    try {
      msg = JSON.parse(ev.data) as WsMessage;
    } catch {
      return; // ignore malformed messages
    }
    for (const listener of messageListeners) listener(msg);
  };
  ws.onclose = () => {
    if (socket !== ws) return; // closed on purpose (last user left)
    socket = null;
    setConnected(false);
    if (users === 0) return;
    attempts += 1;
    retryTimer = window.setTimeout(() => {
      retryTimer = undefined;
      connect();
    }, Math.min(30_000, 1000 * attempts));
  };
  ws.onerror = () => ws.close();
}

function acquire() {
  users += 1;
  if (!socket && retryTimer === undefined) connect();
}

function release() {
  users -= 1;
  if (users > 0) return;
  window.clearTimeout(retryTimer);
  retryTimer = undefined;
  const ws = socket;
  socket = null;
  connected = false;
  ws?.close();
}

/**
 * Calls `onMessage` for every live message and `onConnect` on every
 * (re)connect. Returns whether the socket is connected.
 */
export function useLiveSocket(onMessage: (msg: WsMessage) => void, onConnect?: () => void) {
  const messageRef = useRef(onMessage);
  const connectRef = useRef(onConnect);
  messageRef.current = onMessage;
  connectRef.current = onConnect;
  const [isConnected, setIsConnected] = useState(connected);

  useEffect(() => {
    const onMsg = (msg: WsMessage) => messageRef.current(msg);
    const onStatus = (value: boolean) => {
      setIsConnected(value);
      if (value) connectRef.current?.();
    };
    messageListeners.add(onMsg);
    statusListeners.add(onStatus);
    acquire();
    setIsConnected(connected);
    return () => {
      messageListeners.delete(onMsg);
      statusListeners.delete(onStatus);
      release();
    };
  }, []);

  return isConnected;
}

/**
 * Live messages since mount in a bounded buffer. `maxSize` caps the buffer
 * so the tab doesn't OOM after hours of logs.
 */
export function useLiveStream(maxSize = 500) {
  const [messages, setMessages] = useState<WsMessage[]>([]);
  const connected = useLiveSocket((msg) =>
    setMessages((prev) => {
      const next = [...prev, msg];
      if (next.length > maxSize) next.splice(0, next.length - maxSize);
      return next;
    })
  );
  return { messages, connected };
}
